import { noop } from 'lodash';
import React from 'react';
import { useStyles } from 'uinix-ui';

import { useIntl } from 'react-intl';

import { Box } from '../../../components/Box/Box';
import { Button } from '../../../components/Button/Button';
import { Checkbox } from '../../../components/Checkbox/Checkbox';
import { Layout } from '../../../components/Layout/Layout';
import { Radio } from '../../../components/Radio/Radio';
import { TruncateText } from '../../../components/Text/subcomponents/TruncateText';
import { Cell } from '../Cell/Cell';

const getSortTooltipRange = (cellType, intl) => {
  switch (cellType) {
    case 'link':
    case 'text':
      return [
        intl.formatMessage({
          id: 'Table.headerCell.sortText',
          defaultMessage: 'A',
          description: 'Alphabetically first value for sorting columns',
        }),
        intl.formatMessage({
          id: 'Table.headerCell.sortHigh',
          defaultMessage: 'Z',
          description: 'Alphabetically last value for sorting columns',
        }),
      ];
    case 'datetime':
      return [
        intl.formatMessage({
          id: 'Table.headerCell.sortDate',
          defaultMessage: 'oldest',
          description: 'Oldest value for sorting date columns',
        }),
        intl.formatMessage({
          id: 'Table.headerCell.sortHighDate',
          defaultMessage: 'newest',
          description: 'Most recent value for sorting date columns',
        }),
      ];
    case 'number':
      return [
        intl.formatMessage({
          id: 'Table.headerCell.sortNumber',
          defaultMessage: 'smallest',
          description: 'Smallest or lowest value for sorting number columns',
        }),
        intl.formatMessage({
          id: 'Table.headerCell.sortHighNumber',
          defaultMessage: 'largest',
          description: 'Largest value for sorting number columns',
        }),
      ];
    default:
      return [
        intl.formatMessage({
          id: 'Table.headerCell.sortDefault',
          defaultMessage: 'ascending',
          description: 'Lowest to highest value for sorting default columns',
        }),
        intl.formatMessage({
          id: 'Table.headerCell.sortHighDefault',
          defaultMessage: 'descending',
          description: 'Highest to lowest value for sorting default columns',
        }),
      ];
  }
};

function render(
  {
    disableSelectColumn,
    isMultiSelectColumns,
    isSelected,
    isSorted,
    isSortedDesc,
    resizerProps,
    cellLayoutProps = {},
    title,
    sortProps,
    onSelectColumn,
    styles,
    intl,
  },
  type,
) {
  const [sortLow, sortHigh] = getSortTooltipRange(type, intl);
  const { onClick: onSortClick } = sortProps;

  const sortOption = isSortedDesc ? sortHigh : sortLow;
  const revertedSortOption = isSortedDesc ? sortLow : sortHigh;
  const sortTooltip = isSorted
    ? intl.formatMessage(
        {
          id: 'Table.headerCell.sortedTooltip',
          defaultMessage: 'Sort {sortOption} to {revertedSortOption}',
          description:
            'Tooltip text for sorting a table column. Example: sortOption = "high", revertedSortOption = "low"',
        },
        {
          sortOption,
          revertedSortOption,
        },
      )
    : intl.formatMessage(
        {
          id: 'Table.headerCell.defaultSortTooltip',
          defaultMessage: 'Sort {sortLow} to {sortHigh}',
          description:
            'Tooltip text for sorting a table column. Example: sortLow = "low", sortHigh = "high"',
        },
        {
          sortLow,
          sortHigh,
        },
      );

  const sortIcon = isSorted
    ? isSortedDesc
      ? 'arrow-down'
      : 'arrow-up'
    : undefined;

  const selectColumnsName = 'table-column-select';
  const selectColumnsOption = {
    value: title,
    label: title,
  };

  let content;
  if (onSelectColumn && !disableSelectColumn) {
    if (isMultiSelectColumns) {
      content = (
        <Checkbox
          labelVariant="body-medium"
          name={selectColumnsName}
          option={selectColumnsOption}
          value={isSelected}
          onChange={onSelectColumn}
        />
      );
    } else {
      content = (
        <Radio
          checked={isSelected}
          labelVariant="body-medium"
          name={selectColumnsName}
          option={selectColumnsOption}
          onChange={onSelectColumn}
          value={title}
        />
      );
    }
  } else if (onSortClick) {
    content = (
      <Box
        styles={type === 'loading' ? styles.disabledVisual : null}
        flex="auto"
      >
        <Button
          icon={sortIcon}
          mode="area"
          text={title}
          tooltip={sortTooltip}
          onClick={type !== 'loading' ? onSortClick : noop}
        />
      </Box>
    );
  } else {
    content = (
      <TruncateText textAlign="left" variant="body-medium">
        {title}
      </TruncateText>
    );
  }

  return (
    <Layout
      align="center"
      flex="none"
      justify="inherit"
      spacing={2}
      styles={{
        ':hover .resizer': {
          opacity: 1,
        },
      }}
      {...cellLayoutProps}
    >
      {content}
      {resizerProps && (
        <Box
          styles={{
            display: 'inline-block',
            height: '100%',
            position: 'absolute',
            paddingLeft: 1,
            paddingRight: 1,
            right: 0,
            top: 0,
            touchAction: 'none',
            transform: 'translateX(50%)',
            zIndex: 'forward',
          }}
          {...resizerProps}
        >
          <Box
            className="resizer"
            styles={{
              backgroundColor: 'border.divider',
              height: '100%',
              opacity: 0,
              width: '1px',
            }}
          />
        </Box>
      )}
    </Layout>
  );
}

function HeaderCell({ column }) {
  const {
    canResize,
    cellLayoutProps,
    cellType,
    disableSelectColumn,
    isPrimary,
    isMultiSelectColumns,
    isSelected,
    isSorted,
    isSortedDesc,
    title,
    tooltip,
    totalLeft,
    getHeaderProps,
    getResizerProps,
    getSortByToggleProps,
    onSelectColumn,
    width,
  } = column;

  const intl = useIntl();

  const ariaSort = isSorted
    ? isSortedDesc
      ? 'descending'
      : 'ascending'
    : undefined;

  const styles = useStyles();

  return (
    <Cell
      align="left"
      aria-sort={ariaSort}
      as="th"
      cellProps={{
        cellLayoutProps,
        disableSelectColumn,
        isMultiSelectColumns,
        isSelected,
        isSorted,
        isSortedDesc,
        resizerProps: canResize ? getResizerProps() : null,
        sortProps: getSortByToggleProps(),
        title,
        tooltip,
        onSelectColumn,
        styles,
        intl,
      }}
      flex={width === 'auto' ? 'auto' : 'none'}
      isActive={isSelected}
      isPrimary={isPrimary}
      stickyLeft={isPrimary ? totalLeft : undefined}
      style={getHeaderProps().style}
      type={cellType}
      width={width}
      render={render}
      onClick={onSelectColumn}
    />
  );
}

export default HeaderCell;
